import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { radii, textSize } from 'design/tokens';

/**
 * The instant hover tooltip, and the little "i" that usually carries it.
 *
 * The browser's own `title` is reserved for two jobs: labelling an icon button,
 * and revealing text that had to be truncated. Everything explanatory uses this
 * instead. A `title` waits a second or so before it appears, which is fine for
 * "what does this button do" — you are already hovering, deciding — but wrong
 * for a figure you are scanning past. By the time the browser answers "what is
 * this percentage a share of", you have moved on.
 *
 * The popover is portalled out to the page root and placed against the
 * viewport. Drawn inside its anchor it was clipped by any scrolling list and
 * painted under the next row's sticky day heading, which is how half of it
 * went missing. It never takes pointer events: the tooltip must not be able to
 * sit under the cursor and flicker itself away.
 */

const BASE: React.CSSProperties = {
  position: 'fixed',
  zIndex: 90,
  pointerEvents: 'none',
  background: 'var(--st-ink)',
  color: 'var(--st-card)',
  fontSize: textSize.small,
  borderRadius: radii.sm,
  // Set explicitly because the info icon it hangs from is italic, and a
  // tooltip inheriting that reads like the page is whispering.
  fontStyle: 'normal',
  fontWeight: 400,
};

export interface InstantTipProps {
  /**
   * Below the anchor and left-aligned to it, rather than the default of
   * centred above. Used by the panel-title icons, where "above" would put the
   * tooltip outside the card.
   */
  below?: boolean;
  /**
   * Wrap to this width. Without it the tooltip is a single line, which is what
   * a list of tags or a two-word label wants.
   */
  width?: number;
  children: React.ReactNode;
}

/** Clear of the viewport's edge, and of the anchor. */
const EDGE = 8;
const GAP = 7;

/**
 * A tooltip hung from an element, in viewport coordinates.
 *
 * Measured once it has rendered, so its own size decides the placement: the
 * preferred side unless there is no room for it, then clamped horizontally so
 * a wide tooltip near the edge of the screen slides in rather than off.
 */
export const FloatingTip: React.FC<{
  anchor: HTMLElement | null;
  /** Hang below the anchor; otherwise above it. */
  below?: boolean;
  /** Line up with the anchor's left or right edge; otherwise centred on it. */
  align?: 'left' | 'right' | 'center';
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ anchor, below, align = 'center', style, children }) => {
  const tip = useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !tip.current) return;
    const a = anchor.getBoundingClientRect();
    const t = tip.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fitsAbove = a.top - GAP - t.height >= EDGE;
    const fitsBelow = a.bottom + GAP + t.height <= vh - EDGE;
    const down = below ? fitsBelow || !fitsAbove : !fitsAbove && fitsBelow;
    const top = down ? a.bottom + GAP : a.top - GAP - t.height;
    let left =
      align === 'left' ? a.left - 8 : align === 'right' ? a.right - t.width : a.left + a.width / 2 - t.width / 2;
    left = Math.max(EDGE, Math.min(left, vw - EDGE - t.width));
    setPos({ top, left });
  }, [anchor, below, align, children]);

  if (!anchor) return null;
  return createPortal(
    <span
      ref={tip}
      role="tooltip"
      style={{
        ...BASE,
        ...style,
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        // Measured first, then shown, so it never flashes in the corner.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </span>,
    document.querySelector<HTMLElement>('.st-root') ?? document.body,
  );
};

/**
 * The tooltip for whatever element contains it. A zero-size marker finds that
 * element, so callers keep rendering it inside the anchor as they always have.
 */
export const InstantTip: React.FC<InstantTipProps> = ({ below, width, children }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const mark = useCallback((el: HTMLSpanElement | null) => setAnchor(el?.parentElement ?? null), []);
  return (
    <>
      <span ref={mark} aria-hidden="true" style={{ display: 'none' }} />
      <FloatingTip
        anchor={anchor}
        below={below}
        align={below ? 'left' : 'center'}
        style={
          width
            ? { width, lineHeight: 1.45, padding: '7px 9px', textAlign: 'left' }
            : { whiteSpace: 'nowrap', lineHeight: 1.4, padding: '6px 9px' }
        }
      >
        {children}
      </FloatingTip>
    </>
  );
};

/**
 * A 14px "i" that explains the figure beside it on hover.
 *
 * It owns its own hover state rather than reporting up to the page. The
 * alternative — one `hovered` key on the page, as the prototype does it —
 * re-renders every account row to light up one icon.
 */
export const InfoDot: React.FC<{
  /** What the tooltip says. */
  text: React.ReactNode;
  /** Hang the tooltip below-left instead of centred above. */
  below?: boolean;
  /** Wrap width for the tooltip. */
  width?: number;
  /** Announced to screen readers, which never see the hover. */
  label?: string;
}> = ({ text, below, width = 196, label }) => {
  const [on, setOn] = useState(false);
  return (
    <span
      onMouseEnter={() => setOn(true)}
      onMouseLeave={() => setOn(false)}
      style={{
        position: 'relative',
        width: 14,
        height: 14,
        flex: '0 0 auto',
        borderRadius: '50%',
        boxShadow: 'inset 0 0 0 1px var(--st-edge2)',
        color: 'var(--st-ink4)',
        fontSize: textSize.micro,
        fontWeight: 600,
        fontStyle: 'italic',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default',
      }}
    >
      <span aria-hidden="true">i</span>
      {label != null && <span className="st-sr">{label}</span>}
      {on && (
        <InstantTip below={below} width={width}>
          {text}
        </InstantTip>
      )}
    </span>
  );
};

/**
 * Wraps an element that is itself the tooltip's trigger — a tag cluster, a
 * recurring marker — rather than sitting beside one.
 *
 * `as` keeps it out of the way of the grid: the transactions list positions
 * these markers itself, so the wrapper has to be able to be the very span the
 * layout already expected.
 */
export const HoverTip: React.FC<{
  tip: React.ReactNode;
  /** Nothing to say — render the child without a tooltip or a hover listener. */
  disabled?: boolean;
  width?: number;
  /**
   * Hang below-left instead of centred above. What the anchor's position in
   * its card decides: centred, a wide tooltip on something near the left edge
   * hangs half of itself off the card, which is where the lots table's term
   * chip sits.
   */
  below?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ tip, disabled, width, below, style, children }) => {
  const [on, setOn] = useState(false);
  const leave = useCallback(() => setOn(false), []);
  if (disabled) return <span style={style}>{children}</span>;
  return (
    <span
      onMouseEnter={() => setOn(true)}
      onMouseLeave={leave}
      style={{ position: 'relative', ...style }}
    >
      {children}
      {on && (
        <InstantTip width={width} below={below}>
          {tip}
        </InstantTip>
      )}
    </span>
  );
};

export default InstantTip;
